import { useState } from 'react'
import { motion } from 'framer-motion'
import { Globe, HelpCircle, LogOut, Building2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { useAuthStore } from '@/stores/authStore'
import { useNavigate } from 'react-router-dom'
import { ProfileSection } from '@/components/settingspage/profile'
import { SecuritySection } from '@/components/settingspage/security'
import { NotificationSection } from '@/components/settingspage/notifications'
import { BusinessOnboardingModal } from '@/components/BusinessOnboardingModal'

export function SettingsPage() {
  const [notifications, setNotifications] = useState(true)
  const [biometric, setBiometric] = useState(false)
  const [language, setLanguage] = useState('pt')
  const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false)
  
  const { t } = useTranslation()
  const { logout } = useAuthStore()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const settingsGroups = [
    {
      title: t('settings.preferences', 'Preferências'),
      items: [
        {
          icon: Globe,
          title: t('settings.language'),
          subtitle: t('settings.currentLanguage', 'Português (São Tomé)'),
          action: () => {} // Language settings
        }
      ]
    },
    {
      title: t('settings.support', 'Suporte'),
      items: [
        {
          icon: HelpCircle,
          title: t('settings.help'),
          subtitle: t('settings.faqSupport', 'FAQ e suporte'),
          action: () => navigate('/support')
        }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-background-light">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 p-4">
        <h1 className="text-2xl font-bold text-text-primary">{t('settings.title', 'Configurações')}</h1>
        <p className="text-text-secondary">{t('settings.subtitle', 'Gerencie suas preferências')}</p>
      </div>

      {/* Settings Content */}
      <div className="p-4">

        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
        >
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="font-semibold text-text-primary">{t('settings.account', 'Conta')}</h2>
          </div>
          
          <div className="divide-y divide-gray-100">
            <ProfileSection
              title={t('settings.profile', 'Perfil')}
              subtitle={t('settings.personalInfo', 'Informações pessoais')}
            />
            <SecuritySection
              title={t('settings.security', 'Segurança')}
              subtitle={t('settings.pinBiometric', 'PIN, biometria e autenticação')}
            />
            <NotificationSection
              title={t('settings.notifications', 'Notificações')}
              subtitle={t('settings.alertsReminders', 'Alertas e lembretes')}
            />
            <div className="p-4 flex items-start gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-text-primary">
                  {t('settings.businessTitle', 'Conta empresarial')}
                </h3>
                <p className="text-sm text-text-secondary">
                  {t(
                    'settings.businessSubtitle',
                    'Submeta os dados da sua empresa para aparecer no mapa'
                  )}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setIsBusinessModalOpen(true)}
              >
                {t('settings.businessAction', 'Adicionar empresa')}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Other Settings Groups */}
        <div className="space-y-6">
          {settingsGroups.map((group, groupIndex) => (
            <motion.div
              key={group.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (groupIndex + 1) * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                <h2 className="font-semibold text-text-primary">{group.title}</h2>
              </div>
              
              <div className="divide-y divide-gray-100">
                {group.items.map((item, itemIndex) => {
                  const IconComponent = item.icon
                  
                  return (
                    <motion.button
                      key={item.title}
                      onClick={item.action}
                      className="w-full flex items-center space-x-3 p-4 hover:bg-gray-50 transition-colors"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: ((groupIndex + 1) * 0.1) + (itemIndex * 0.05) }}
                    >
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <IconComponent className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-medium text-text-primary">{item.title}</h3>
                        <p className="text-sm text-text-secondary">{item.subtitle}</p>
                      </div>
                      <div className="text-text-secondary">
                        <span>→</span>
                      </div>
                    </motion.button>
                  )
                })}
              </div>
            </motion.div>
          ))}
        </div>


        {/* Logout Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-6"
        >
          <Button
            variant="danger"
            onClick={handleLogout}
            className="w-full"
          >
            <LogOut className="h-4 w-4 mr-2" />
            {t('auth.logout')}
          </Button>
        </motion.div>
      </div>

      <BusinessOnboardingModal
        isOpen={isBusinessModalOpen}
        onClose={() => setIsBusinessModalOpen(false)}
        onComplete={() => setIsBusinessModalOpen(false)}
      />
    </div>
  )
}



